/**
 * Waffle flags should be added to this list before usage in JS/TS.
 * Each entry should:
 *   - Be given a descriptive key name
 *   - Include a description of what the flag gates
 *   - Link to a ticket for flag removal, or an explanation as to why there isn't one
 *
 * Note: Though this enum can (and should!) be used from JS/Ractive, its usage is only
 * _enforced_ in TS code, so it's still recommended to search for both the enum AND the
 * "raw" waffle flag string for removal, unless you are sure you know otherwise.
 */
enum WaffleFlag {
  // Old flags without owners
  // ------------------------
  // Enables an emoji picker add-on to the composer (no removal ticket known)
  EmojiPicker = 'emoji-picker-widget',
  // Allows us to set a base URL for static lists API that is on a different
  // domain (WEB-888)
  StaticListsCORS = 'static-lists-cors-flag',
  // Controls access to the SFMC integrations, SFMC AppExchange and RTDS. Used by Bizops for contracted access so don't remove.
  CONNECT_SFMC_FLAG = 'connect-sfmc-flag',

  // ADVSEG flags
  // ------------
  // Enable Channel coordination in the builder (removal : ADVSEG-1027)
  BuilderChannelCoordination = 'builder-channel-coordination-flag',
  // Enable Push local time for A/B testing (removal : ADVSEG-1815)
  PushLocalTimeForExperiment = 'push-local-time-for-experiment-flag',
  // Enable throttling for A/B testing (removal : ADVSEG-1894)
  ThrottlingForExperiment = 'throttling-for-experiment-flag',
  // Enable NEW ACTIONS IN  WYSIWYG (removal : ADVSEG-2272)
  ExtraActionsInWYSIWYG = 'extra-actions-in-wysiwyg-flag',
  // Enable contact checkbox in feedback modal (removal: ADVSEG-2362)
  ContactCheckBoxFeedbackFlag = 'contact-checkbox-feedback-flag',
  // Add experiment data to survey aggregate reports (removal: ADVSEG-4620)
  SurveysInScenesAggregateReport = 'surveys-in-scenes-aggregate-report',
  // Enables the use of the legavy surveys (Removal: ADVSEG-4740)
  LegacySurveyMigration = 'legacy_survey_migration',
  // Enables recommended and deprecated option bages (Removal: ADVSEG-7304)
  RecommendedOptionBadge = 'recommended-option-badge',
  // Enables new Iaa debugger in the control panels
  IaaDebugger = 'iaa-debugger',
  //Hide the survey deprecation warning (removal: ADVSEG-6986)
  HideSurveyDeprecationWarning = 'hide_survey_deprecation_warning',
  // Hide AMP support in web configs (removal: ADVSEG-7122)
  HideAMPWebOptionFlag = 'hide-AMP-web-option-flag',
  // To be able to display scene TreeView (removal: ADVSEG-7299)
  SceneTreeViewFlag = 'scene-tree-view-flag',
  // Mark text content as headings in editor (removal: ADVSEG-7283)
  MarkTextPropertiesAsHeading = 'mark-text-properties-as-heading',
  // Enable Markdown support in thomas preview (removal: ADVSEG-7357)
  SceneMarkdownRendering = 'scene-markdown-rendering',
  // To be able to have media, containers, views clickable (removal: ADVSEG-7355)
  SceneAlignment = 'scene-alignment',
  // Enable resizing of dismiss button (removal: ADVSEG-7496)
  SceneDismissButtonSize = 'scene_dismiss_button_size',
  // Enable custom breakpoints (removal: ADVSEG-7623)
  SceneCustomBreakpoint = 'scene-custom-breakpoint',
  // Add shadow support for modals (removal: ADVSEG-7615)
  SceneModalShadows = 'scene-modal-shadows',
  // Add interactive mode for scene preview (removal: ADVSEG-7692)
  SceneInteractiveMode = 'scene-interactive-mode',
  // Add email collect content item for scene (removal: ADVSEG-7652)
  SceneEmailCollect = 'scene-email-collect',
  // Add embedded in the content editor + add carousel of embedded in the message overview (removal: ADVSEG-7751)
  EmbeddedCarousel = 'embedded-carousel',
  // Enables adding custom view content items to the IAX content composer (removal: ADVSEG-8107)
  SceneCustomView = 'scene-custom-view',
  // Adds experience type option to the message overview filters (removal: ADVSEG-7816)
  ExperienceTypeFilters = 'experience-type-filters',
  // Allows customers to add branching logic to their scenes (removal: ADVSEG-7699)
  SceneBranchingLogic = 'scene-branching-logic',
  // Allows to set up styles for the text inputs
  TextInputSettings = 'text-input-settings',
  // Add property wrapping in the NPS (removal: ADVSEG-7899)
  NPSWrapping = 'nps-wrapping',
  // Add a name to a card (removal: ADVSEG-7895)
  SceneEditableScreenName = 'scene-editable-screen-name',
  // Add short text content items (removal: ADVSEG-7938)
  SceneTextInputItem = 'scene-text-input-item',
  // Add video options in the content editor (removal: ADVSEG-7954)
  SceneVideoControls = 'scene-video-controls',
  // Add Sms input item in scene (removal: ADVSEG-7986)
  SceneSmsInputItem = 'scene-sms-input-item',
  // Add new design for View Styles settings (removal: ADVSEG-7988)
  SceneViewStylesUpdates = 'scene-view-styles-updates',
  // change the default layout of Scenes (removal: ADVSEG-8113)
  UpdateSceneDefaultLayouts = 'update-scene-default-layouts',
  // Email Add registration options to UI (removal: ADVSEG-7998)
  SceneEmailRegistration = 'scene-email-registration',
  // Add form validation text input to the button (removal: )
  SceneFormValidationAction = 'scene-form-validation-action',
  // Enable Web Scene Linking (removal: ADVSEG-8294)
  WebSceneLinking = 'web-scene-linking',
  // Vimeo support (removal: ADVSEG-8038)
  SceneVimeoSupport = 'scene-vimeo-support',
  // enables GA features for branching logic (removal: ADVSEG-8344)
  SceneBranchingLogicGA = 'scene-branching-logic-ga',
  // AI scene generation prototype (removal: ADVSEG-8408)
  AiSceneGeneration = 'ai-scene-generation',
  // add root element for the tree view and move root settings to the right (removal: ADVSEG-8320)
  SceneRootLayoutDesignUpdates = 'scene-root-layout-design-updates',
  // add first level items % height in story mode (removal: )
  SceneStoryModeHeightFix = 'scene-storymode-height-fix',
  // enables new Layouts for branching logic (removal: )
  SceneBranchingLogicLayout = 'scene-branching-logic-layout',

  // CHAN flags
  // ----------
  // Enables Generic pass type for google wallet (removal: CHAN-4603)
  GoogleGeneric = 'google-generic-flag',
  // Enable Google Template Overrides for Loyalty Passes (removal: CHAN-4429)
  LoyaltyOverrides = 'google-overrides-loyalty-flag',
  // Enable template duplication in wallet dashboard (removal: CHAN-5131)
  WalletTemplateDuplication = 'wallet-template-duplication-flag',
  // New app settings dashboard
  ControlPanelDarkMatterSms = 'control-panel-dark-matter-sms',
  // Bulk add/remove tags (removal: CHAN-4392)
  ContactManagementTagUpload = 'contact-management-tag-upload-flag',
  // Contact Management Named User messages embedded looker sso url
  // (removal CHAN-4620)
  NamedUserMessagesDemo = 'named-user-messages-demo-flag',
  // Adding email as a localizable platform (removal: CHAN-5077)
  EmailLocalization = 'email-localization',
  // Enables preference center channel opt-in (removal: CHAN-6440)
  PreferenceCenterChannelOptInFlag = 'preference-center-channel-opt-in-flag',
  // Sendgrid config in Flight Deck (removal: CHAN-6490)
  SendgridFlightDeck = 'sendgrid-flight-deck-flag',
  // SMS length warning for Keyword Responses
  KeywordReponseLengthWarning = 'keyword-response-length-warning',
  // Display a heat map of email click using named links reporting data (CHAN-7092)
  EmailHeatMap = 'email-heat-map',
  // Allows MMS messages to support vcard file formats
  MmsVcardUpload = 'mms-vcard-upload',
  // Allow "contains" operator for email domain default property
  EmailDomainContains = 'email-domain-contains',
  // Enables early access to Stensul integration (removal: CHAN-7290)
  StensulIntegration = 'stensul-integration',
  // Give us a safe way to experiment with looker caching (removal: CHAN-6686)
  LookerCacheExperiments = 'looker-cache-experiments',
  // Feature making it easier to preview content and send tests (removal: CHAN-7558)
  EmailProofingTesting = 'email-proofing-testing',
  // Show Deep Link option
  ShowDeepLinkOption = 'show-deep-link-option',
  // Part two for feature making it easier to preview content and send tests (removal: CHAN-7596)
  EmailProofingTestingPartTwo = 'email-proofing-testing-part-2',
  // Removal of the floating Preview Data widget (removal: CHAN-7776)
  EmailProofingPreviewButton = 'email-proofing-preview-button',
  // (removal CHAN-7619)
  WalletLandingPages = 'wallet-landing-pages',
  // Show Click Tracking option (removal: CHAN-7671)
  ShowClickTrackingOption = 'show-click-tracking-option',
  // Disable safeHtml option in Unlayer (removal: CHAN-7678)
  DisableSafeHtmlUnlayer = 'disable-safe-html-unlayer',
  UnlayerImportExport = 'unlayer-import-export',

  // EXP flags
  // ---------
  // Show internal dropdown to configure a flag's TTL
  FeatureFlagCacheConfig = 'feature-flag-cache-config',
  // Show Feature Flags Image Uploader (removal: EXP-859)
  FeatureFlagImageUploader = 'ff-image-uploader',
  // Show Experiment Groups pages (removal: EXP-833)
  ExperimentGroups = 'experiment-groups',

  // ORC flags
  // ---------
  // Generated reactivation exits for reactivation journeys (ORC-2344)
  GeneratedReactivationCancellationExits = 'generated-reactivation-cancellation',
  // Enables doing A/B tests on delays between messages in journeys. (removal: ORC-2679)
  JourneyABTestDelay = 'journey-ab-test-delay',
  // Enables new enhanced display of sequence reporting (removal: ORC-3990)
  SequenceReportingQ32022 = 'sequence-reporting-q3-2022',
  // Enables Journey Map Drawer Improvements (Removal: ORC-4593)
  JourneyMapDrawerImprovements = 'journey-map-drawer-improvements',
  AIJourneysPhase2 = 'ai-journeys-phase2',
  // Approval workflows (Removal: ORC-6085)
  ApprovalWorkflows = 'approval-workflows',
  // Automatically store drafts to track history for all message types (Removal: ORC-6085)
  ApprovalWorkflowAutoDraft = 'approval-workflow-auto-draft',
  // Approval workflows in Product (Removal: ORC-6172)
  ApprovalWorkflowsInProduct = 'approval-workflows-in-product',
  // AI-powered Unlayer template generator (Removal: ORC-6376)
  EmailTemplateGenerator = 'email-template-generator',
  // Brand Guidelines Phase 2 (Removal: ORC-6507)
  BrandGuidelinesPhase2 = 'brand-guidelines-phase2',

  // PINT flags
  // ----------
  // SFTP Configuration in APIs & Integrations
  SftpConfiguration = 'sftp-configuration-flag',
  // Enbables Named User Association list upload (removal: PINT-1575)
  NamedUserAssociationListUpload = 'named-user-association-list-upload',
  // Enables the new GA4 integration and hides the old (removal: PINT-1861)
  GoogleAnalytics4 = 'google-analytics-4',
  // Enables early access to Datashare partner page (removal: PINT-2131)
  Datashare = 'pint-datashare',
  // Enables AWS S3 Incoming integrations (removal: PINT-2350)
  S3Incoming = 's3-incoming',

  // TPS flags
  // ---------
  // Hides legacy rich push editor unless an app has custom Rich Push templates. (removal: TPS-689)
  HideLegacyRichPushEditorUnlessAppHasCustomTemplates = 'hide-legacy-rich-push-editor',
  // Enables the subscription list reports page (Removal: TPS-3764)
  SubscriptionListReports = 'subscription-list-reports',
  // Enables advanced Goal report filters (removal: TPS-4339)
  GoalReportFilters = 'goal-report-filters',
  // Enables goals tracking multiple custom events (removal: TPS-4479)
  MultiEventGoals = 'multi-event-goals',
  // Enables reporting hub v1 features (removal: TPS-4576)
  ReportingHubV1 = 'reporting-hub-v1',
  // Display configured single value comparison values (removal: TPS-5072)
  AppHealthSingleValueComparison = 'app-health-single-value-comparison',
  // Enable Audience limits level selection (removal: TPS-5148)
  AudienceLimitsLevelSelection = 'audience-limits-level',
  // Display activation drop-off chart (removal: TPS-5072)
  AppHealthActivationDropOff = 'app-health-activation-drop-off',
  // Display activation never reopened chart (removal: TPS-5072)
  AppHealthActivationNeverReopened = 'app-health-activation-never-reopened',
  // Use new app health dashboard layout UI elements (removal: TPS-5072)
  AppHealthUIUpdate = 'app-health-ui-update',
  // Display embedded scene in the app health dashboard (removal: TPS-5488)
  AppHealthEmbeddedScene = 'app-health-embedded-scene',
  // Show goal performance on per-message reports (removal: TPS-5459)
  MessageReportsGoals = 'message-reports-goals',
  // Enable new User Segmentation Audience page for Goatzart (removal: TPS-5672)
  UserSegmentationComposer = 'user-segmentation-composer',
  // Enable Gartner demo data (removal: TPS-5723)
  GartnerDemoData = 'gartner-demo-data',
  // Enable date type in JSON attributes
  JsonAttributeDateType = 'json-attribute-date-type',

  // WPLAT flags
  // -----------
  // Disables operator selection for static lists in the segment builder (WPLAT-711)
  SegmentListOperatorDisabled = 'segment-list-operator-disabled',

  // TOOLS flags
  // -----------
  // Olympics Delivery Time Dashboard (Added: TOOLSLIBS-1886 | Removal: TOOLSLIBS-1914)
  OlympicsDeliveryTime = 'olympics-delivery-time',
  // Flight Deck 1.4 (codename Tiger | Removal: TOOLSLIBS-2160)
  FD14Tiger = 'fd-14-tiger',
  // Separate flag for trial end dates (Removal: TOOLSLIBS-2160)
  TrialEndDates = 'trial-end-dates',
  // Zero Copy Data (Added: TOOLSLIBS-2353 | Removal: TOOLSLIBS-2369)
  FDZeroCopyData = 'fd-zero-copy-data',

  // DS flags
  // -----------

  // BEES flags
  // -----------
  // The general purpose flag for identifying a user as part of the BEES group. Used to control settings like the warning bar (Removal: nah)
  Bees = 'bees',
  // Allows users to turn off the bees UI even while in the user group (Removal: BEES-22)
  BeesOptOut = 'bees-opt-out',
  // Delegates nav bar rendering to the AirshipPage component instead of django templates (Removal: BEES-3)
  ReactNav = 'react-nav',
  // Allows access to experimental campaigns homepage (Removal: BEES-36)
  CampaignsHomepage = 'campaigns-homepage',
  // Updates custom events listing page to use new DataTable component (Removal: BEES-42)
  EventsDataTable = 'events-data-table',
  // Use React API Token Settings page (Removal: BEES-133)
  ReactApiTokenSettings = 'react-api-token-settings',
  // Use React Named User Settings page (Removal: BEES-134)
  ReactNamedUserSettings = 'react-named-user-settings',
  // Use React SFTP Settings page (Removal: BEES-135)
  ReactSftpSettings = 'react-sftp-settings',
  // Use React XDF Settings page (Removal: BEES-136)
  ReactXdfSettings = 'react-xdf-settings',
  // Use React Couponing Settings page (Removal: BEES-137)
  ReactCouponingSettings = 'react-couponing-settings',

  // ^^^ Add new flags to your team's section ^^^
  // Be sure to add new flags to airship/polybius/flags.py, otherwise they
  // will not be created by the sync_flags management command!
}

export default WaffleFlag
